/**
 * サービスプロバイダー(SP)。SaaS を SSO に載せるための登録画面。
 *
 * 実体は Keycloak の SAML / OIDC クライアントで、この画面は**フォームの値**を送るだけ。
 * Keycloak の属性に組み替えるのは API(`server/app/idp/sp_service.py`)の仕事。
 * 「リンク」はポータルに置く外部リンクで、Keycloak は関係ない。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { IdpInfo, ProviderForm, SamlAttributeRow, ServiceProvider } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  errorText,
  useIdpDownload,
  useIdpInfo,
  useIdpPerms,
  useInvalidateIdp,
  useProvider,
  useProviders,
} from "./hooks";
import {
  AUTHN_CONTEXTS,
  KIND_LABEL,
  LOGO_ACCEPT,
  LOGO_MAX_BYTES,
  NAME_ID_ATTRIBUTES,
  NAME_ID_FORMATS,
  SECRET_MASK,
  SESSION_HOURS_DEFAULT,
  SIGNATURE_ALGORITHMS,
  USER_FIELDS,
  statusColor,
} from "./spOptions";

const { Text, Paragraph } = Typography;

/** 画面のフォームの値。複数行の入力は改行区切りの文字列で持つ。 */
type Values = {
  name: string;
  display_name?: string;
  acs_url?: string;
  extra_acs_urls?: string;
  sp_entity_id?: string;
  name_id_attribute?: string;
  name_id_format?: string;
  login_url?: string;
  relay_state?: string;
  signature_algorithm?: string;
  authn_context?: string;
  sso_url_name?: string;
  session_max_hours?: number;
  portal_visible?: boolean;
  enabled?: boolean;
  logo?: string;
  sort_order?: number;
  attributes?: SamlAttributeRow[];
  signing_certificate?: string;
  client_id?: string;
  redirect_uris?: string;
  pkce?: string;
  post_logout_url?: string;
};

const lines = (v?: string): string[] =>
  (v ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

/** フォームの値 → API に送る形。 */
function toForm(kind: string, v: Values): ProviderForm {
  const common = {
    name: v.name,
    // 画面には出さないが、API から来た値はそのまま返す(seed が付けた表示名を消さない)
    display_name: v.display_name ?? "",
    login_url: v.login_url ?? "",
    portal_visible: v.portal_visible ?? true,
    enabled: v.enabled ?? true,
    logo: v.logo ?? "",
    sort_order: v.sort_order ?? 100,
  };
  if (kind === "link") return common;
  if (kind === "oidc")
    return {
      ...common,
      client_id: v.client_id ?? "",
      redirect_uris: lines(v.redirect_uris),
      pkce: v.pkce ?? "S256",
      post_logout_url: v.post_logout_url ?? "",
    };
  return {
    ...common,
    sp_entity_id: v.sp_entity_id ?? "",
    acs_url: v.acs_url ?? "",
    extra_acs_urls: lines(v.extra_acs_urls),
    name_id_attribute: v.name_id_attribute ?? "username",
    name_id_format: v.name_id_format ?? "unspecified",
    relay_state: v.relay_state ?? "",
    signature_algorithm: v.signature_algorithm ?? "RSA_SHA256",
    authn_context: v.authn_context ?? "PasswordProtectedTransport",
    sso_url_name: v.sso_url_name ?? "",
    session_max_hours: v.session_max_hours ?? SESSION_HOURS_DEFAULT,
    signing_certificate: v.signing_certificate ?? "",
    attributes: (v.attributes ?? []).filter((a) => a && a.saml_name && a.user_field),
  };
}

/** API から来たフォームの値 → 画面の値。 */
function toValues(form: ProviderForm): Values {
  return {
    ...form,
    extra_acs_urls: (form.extra_acs_urls ?? []).join("\n"),
    redirect_uris: (form.redirect_uris ?? []).join("\n"),
    attributes: form.attributes ?? [],
  };
}

const EMPTY: Record<string, Values> = {
  saml: {
    name: "",
    name_id_attribute: "username",
    name_id_format: "unspecified",
    signature_algorithm: "RSA_SHA256",
    authn_context: "PasswordProtectedTransport",
    session_max_hours: SESSION_HOURS_DEFAULT,
    portal_visible: true,
    enabled: true,
    sort_order: 100,
    attributes: [],
  },
  oidc: { name: "", pkce: "S256", portal_visible: true, enabled: true, sort_order: 100 },
  link: { name: "", portal_visible: true, enabled: true, sort_order: 100 },
};

/** ロゴ。data URL のまま持つ(64KB まで)。 */
function LogoField({ value, onChange }: { value?: string; onChange?: (v: string) => void }) {
  return (
    <Space direction="vertical">
      <Space>
        <Upload
          accept={LOGO_ACCEPT}
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file: File) => {
            if (file.size > LOGO_MAX_BYTES) {
              void message.error(`ロゴは ${LOGO_MAX_BYTES / 1024}KB まで`);
              return Upload.LIST_IGNORE;
            }
            const reader = new FileReader();
            reader.onload = () => onChange?.(String(reader.result ?? ""));
            reader.readAsDataURL(file);
            return Upload.LIST_IGNORE; // 送らない。data URL にして値にするだけ
          }}
        >
          <Button>ロゴを選ぶ</Button>
        </Upload>
        {value && <Button onClick={() => onChange?.("")}>外す</Button>}
      </Space>
      {value ? (
        <img src={value} alt="ロゴ" style={{ maxWidth: 140, maxHeight: 90 }} />
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          140×90 程度・png / jpg / svg・64KB まで
        </Text>
      )}
    </Space>
  );
}

/** 種別ごとのフォーム。項目の順番は SP 側の設定画面に合わせてある。 */
function ProviderFields({ kind }: { kind: string }) {
  const url = { type: "url" as const, message: "http(s) で始まる URL を入れる" };
  return (
    <>
      <Form.Item
        name="name"
        label="サービス名"
        rules={[{ required: true, message: "サービス名は必須" }]}
      >
        <Input placeholder="kintone" />
      </Form.Item>

      {kind === "saml" && (
        <>
          <Form.Item
            name="acs_url"
            label="ACS URL"
            rules={[{ required: true, message: "ACS URL は必須" }, url]}
          >
            <Input placeholder="https://example.com/saml/acs" />
          </Form.Item>
          <Form.Item
            name="extra_acs_urls"
            label="追加 ACS URL"
            extra="1行に1つ。SP が複数のエンドポイントを持つときだけ"
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="sp_entity_id"
            label="SP Issuer(Audience)"
            rules={[{ required: true, message: "SP Issuer は必須" }]}
          >
            <Input placeholder="https://example.com/sp" />
          </Form.Item>
          <Form.Item name="name_id_attribute" label="Name ID 属性">
            <Select options={NAME_ID_ATTRIBUTES} />
          </Form.Item>
          <Form.Item name="name_id_format" label="Name ID 形式">
            <Select options={NAME_ID_FORMATS} />
          </Form.Item>
          <Form.Item name="login_url" label="ログイン URL" rules={[url]}>
            <Input placeholder="https://example.com/" />
          </Form.Item>
          <Form.Item name="relay_state" label="Relay State">
            <Input />
          </Form.Item>
          <Form.Item name="signature_algorithm" label="署名方式">
            <Select options={SIGNATURE_ALGORITHMS} />
          </Form.Item>
          <Form.Item name="authn_context" label="AuthnContext">
            <Select options={AUTHN_CONTEXTS} />
          </Form.Item>
          <Form.Item
            name="sso_url_name"
            label="固有番号"
            extra="ダイレクトログイン URL(.../protocol/saml/clients/<固有番号>)に使う"
          >
            <Input placeholder="kintone" />
          </Form.Item>
          <Form.Item name="session_max_hours" label="セッション有効時間(時間)">
            <InputNumber min={1} max={720} />
          </Form.Item>
        </>
      )}

      {kind === "oidc" && (
        <>
          <Form.Item
            name="client_id"
            label="client_id"
            rules={[{ required: true, message: "client_id は必須" }]}
          >
            <Input placeholder="grafana" />
          </Form.Item>
          <Form.Item name="redirect_uris" label="リダイレクト URI" extra="1行に1つ">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="login_url" label="ログイン URL" rules={[url]}>
            <Input />
          </Form.Item>
          <Form.Item name="pkce" label="PKCE">
            <Select
              options={[
                { value: "S256", label: "S256(推奨)" },
                { value: "", label: "使わない" },
              ]}
            />
          </Form.Item>
          <Form.Item name="post_logout_url" label="ログアウト後の URL" rules={[url]}>
            <Input />
          </Form.Item>
        </>
      )}

      {kind === "link" && (
        <Form.Item
          name="login_url"
          label="リンク先 URL"
          rules={[{ required: true, message: "リンク先 URL は必須" }, url]}
        >
          <Input placeholder="https://wiki.example.jp/" />
        </Form.Item>
      )}

      <Form.Item name="portal_visible" label="ポータル表示" valuePropName="checked">
        <Switch checkedChildren="出す" unCheckedChildren="出さない" />
      </Form.Item>
      <Form.Item name="enabled" label="状態">
        <Select
          options={[
            { value: true, label: "有効" },
            { value: false, label: "無効" },
          ]}
        />
      </Form.Item>
      <Form.Item name="logo" label="ロゴ">
        <LogoField />
      </Form.Item>

      {kind === "saml" && (
        <Form.Item label="属性設定" extra="SP に渡す SAML 属性。名前とユーザー項目の対応">
          <Form.List name="attributes">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: "100%" }}>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline">
                    <Form.Item {...field} name={[field.name, "saml_name"]} noStyle>
                      <Input placeholder="SAML 属性名(Email)" style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, "user_field"]} noStyle>
                      <Select
                        placeholder="ユーザー項目"
                        style={{ width: 260 }}
                        options={USER_FIELDS}
                      />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>
                      削除
                    </Button>
                  </Space>
                ))}
                <Button onClick={() => add({ saml_name: "", user_field: "email" })}>
                  属性を追加
                </Button>
              </Space>
            )}
          </Form.List>
        </Form.Item>
      )}

      <Form.Item name="sort_order" label="並び順" extra="小さいほどポータルの前に出る">
        <InputNumber min={0} max={9999} />
      </Form.Item>
      <Form.Item name="signing_certificate" hidden>
        <Input />
      </Form.Item>
    </>
  );
}

/**
 * コピーできる値。
 * (Descriptions.Item は antd が直の子として読むので、包まずに中身だけ差す)
 */
function CopyValue({ label, value }: { label: string; value: string | null }) {
  return (
    <Flex gap={8} align="center" wrap>
      <Text code style={{ wordBreak: "break-all" }}>
        {value || "—"}
      </Text>
      {value && (
        <Button
          size="small"
          onClick={() => {
            void navigator.clipboard
              .writeText(value)
              .then(() => message.success(`${label} をコピーした`))
              .catch(() => message.error("コピーできなかった"));
          }}
        >
          コピー
        </Button>
      )}
    </Flex>
  );
}

/** SP 側に登録してもらう値。ダウンロードは viewer でもできる。 */
function IdpInfoPanel({ id, kind }: { id: number; kind: string }) {
  const info = useIdpInfo(id);
  const download = useIdpDownload();
  const data: IdpInfo | undefined = info.data;
  return (
    <Card size="small" title="IdP 情報" data-testid="idp-info">
      {info.isError && (
        <Alert type="error" showIcon message="IdP 情報を取得できなかった"
               description={errorText(info.error)} />
      )}
      {data && !data.configured && (
        <Alert type="warning" showIcon
               message="Keycloak が未設定(SaaSコネクタ設定で url / realm / 管理者を入れる)" />
      )}
      {data && (
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="IdP Issuer">
            <CopyValue label="IdP Issuer" value={data.issuer} />
          </Descriptions.Item>
          <Descriptions.Item label="SSO URL">
            <CopyValue label="SSO URL" value={data.sso_url} />
          </Descriptions.Item>
          <Descriptions.Item label="サインアウト URL">
            <CopyValue label="サインアウト URL" value={data.logout_url} />
          </Descriptions.Item>
          <Descriptions.Item label="ダイレクトログイン URL">
            <CopyValue label="ダイレクトログイン URL" value={data.direct_login_url} />
          </Descriptions.Item>
        </Descriptions>
      )}
      {kind === "saml" && (
        <Space style={{ marginTop: 12 }} wrap>
          <Button onClick={() => void download("/api/idp/providers/idp-info/metadata.xml")}>
            メタデータ XML をダウンロード
          </Button>
          <Button onClick={() => void download("/api/idp/providers/idp-info/signing.crt")}>
            SAML 署名証明書をダウンロード
          </Button>
        </Space>
      )}
    </Card>
  );
}

/** OIDC の client_id と secret。secret は伏字で、再発行した直後だけ平文を見せる。 */
function OidcSecret({ id, clientId }: { id: number; clientId: string | null }) {
  const api = useApi();
  const { canAdmin } = useIdpPerms();
  const rotate = useMutation({
    mutationFn: () => api.rotateOidcSecret(id),
    onSuccess: (res) => {
      Modal.info({
        title: "新しい client secret",
        content: (
          <Paragraph copyable={{ text: res.secret }} style={{ wordBreak: "break-all" }}>
            {res.secret}
          </Paragraph>
        ),
        okText: "閉じる",
      });
    },
    onError: (e) => void message.error(errorText(e)),
  });
  return (
    <Card size="small" title="OIDC クライアント">
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="client_id">
          <CopyValue label="client_id" value={clientId} />
        </Descriptions.Item>
        <Descriptions.Item label="client secret">
          <Flex gap={8} align="center">
            <Text code>{SECRET_MASK}</Text>
            {canAdmin && (
              <Popconfirm
                title="secret を再発行する"
                description="いまの secret はすぐ使えなくなる。SP 側の差し替えが要る"
                okText="再発行する"
                cancelText="やめる"
                onConfirm={() => rotate.mutate()}
              >
                <Button size="small" loading={rotate.isPending}>
                  再発行
                </Button>
              </Popconfirm>
            )}
          </Flex>
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

type DrawerState = { id: number | null; kind: string; initial?: Values } | null;

/** 追加と編集の両方。`id` が null なら追加。 */
function ProviderDrawer({ state, onClose }: { state: DrawerState; onClose: () => void }) {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const { canAdmin } = useIdpPerms();
  const [form] = Form.useForm<Values>();
  const detail = useProvider(state?.id ?? null);
  const kind = state?.kind ?? "saml";
  const d = detail.data;

  useEffect(() => {
    if (!state) return;
    if (state.id == null) form.setFieldsValue({ ...EMPTY[kind], ...(state.initial ?? {}) });
    else if (d) form.setFieldsValue(toValues(d.form));
  }, [state, d, form, kind]);

  const save = useMutation({
    mutationFn: (v: Values) =>
      state?.id == null
        ? api.createProvider(kind, toForm(kind, v))
        : api.updateProvider(state.id, toForm(kind, v)),
    onSuccess: (res) => {
      invalidate();
      if (res.secret) {
        Modal.info({
          title: "client secret(この1回だけ表示する)",
          content: (
            <Paragraph copyable={{ text: res.secret }} style={{ wordBreak: "break-all" }}>
              {res.secret}
            </Paragraph>
          ),
          okText: "閉じる",
        });
      } else {
        void message.success(state?.id == null ? "追加した" : "保存した");
      }
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteProvider(state!.id!),
    onSuccess: (res) => {
      invalidate();
      if (res.warning) void message.warning(res.warning);
      else void message.success(`${res.name} を削除した`);
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const title =
    state?.id == null
      ? `サービスプロバイダーの追加(${KIND_LABEL[kind] ?? kind})`
      : `${d?.display_name ?? ""}(${KIND_LABEL[kind] ?? kind})`;

  return (
    <Drawer
      open={!!state}
      width={720}
      onClose={onClose}
      title={title}
      destroyOnClose
      extra={
        canAdmin && (
          <Space>
            {state?.id != null && (
              <Popconfirm
                title="このサービスプロバイダーを削除する"
                description="Keycloak のクライアントも消える(戻せない)"
                okText="削除する"
                cancelText="やめる"
                onConfirm={() => remove.mutate()}
              >
                <Button danger loading={remove.isPending}>
                  削除
                </Button>
              </Popconfirm>
            )}
            <Button type="primary" loading={save.isPending} onClick={() => void form.submit()}>
              {state?.id == null ? "追加する" : "保存"}
            </Button>
          </Space>
        )
      }
    >
      {detail.isError && (
        <Alert type="error" showIcon message="取得できなかった"
               description={errorText(detail.error)} />
      )}
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {state?.id != null && d && (
          <Flex gap={8} wrap align="center">
            <Tag color={statusColor(d.status)}>{d.status}</Tag>
            <Text type="secondary">{d.kc_client_id ?? "Keycloak には作らない種別"}</Text>
          </Flex>
        )}
        <Form
          form={form}
          layout="vertical"
          disabled={!canAdmin}
          onFinish={(v: Values) => save.mutate(v)}
        >
          <ProviderFields kind={kind} />
        </Form>
        {state?.id != null && kind === "oidc" && (
          <OidcSecret id={state.id} clientId={d?.kc_client_id ?? null} />
        )}
        {state?.id != null && kind !== "link" && <IdpInfoPanel id={state.id} kind={kind} />}
      </Space>
    </Drawer>
  );
}

/** 4経路の選び直し(SAML 手動 / SAML メタデータ / OIDC / ポータルリンク)。 */
function AddModal({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (kind: string, initial?: Values) => void;
}) {
  const api = useApi();
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const res = await api.importSamlMetadata(file);
      onPick("saml", { ...EMPTY.saml, ...toValues(res.form) });
      void message.success("メタデータを読み込んだ。内容を確かめてから追加する");
    } catch (e) {
      void message.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="サービスプロバイダーの追加"
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Button block onClick={() => onPick("saml")}>
          SAML(手動)
        </Button>
        <Upload
          accept=".xml,text/xml,application/xml"
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file: File) => {
            void upload(file);
            return Upload.LIST_IGNORE;
          }}
        >
          <Button block loading={busy}>
            SAML(メタデータをアップロード)
          </Button>
        </Upload>
        <Button block onClick={() => onPick("oidc")}>
          OIDC
        </Button>
        <Button block onClick={() => onPick("link")}>
          ポータルリンク
        </Button>
      </Space>
    </Modal>
  );
}

export function ProvidersPage() {
  const { canAdmin } = useIdpPerms();
  const [qInput, setQInput] = useState("");
  const [filters, setFilters] = useState({ q: "", kind: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [adding, setAdding] = useState(false);
  const [drawer, setDrawer] = useState<DrawerState>(null);

  const query = useProviders({ ...filters, limit: pageSize, offset: (page - 1) * pageSize });

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const columns: ColumnsType<ServiceProvider> = [
    {
      title: "表示名",
      width: 200,
      dataIndex: "display_name",
      render: (v: string, row) => (
        <a onClick={() => setDrawer({ id: row.id, kind: row.kind })}>{v}</a>
      ),
    },
    {
      title: "種別",
      dataIndex: "kind_label",
      width: 140,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: "ポータル表示",
      dataIndex: "portal_visible",
      width: 130,
      render: (v: boolean) => (v ? "出す" : "出さない"),
    },
    {
      title: "状態",
      dataIndex: "status",
      width: 140,
      render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag>,
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          サービスプロバイダー
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" onClick={() => setAdding(true)}>
            サービスプロバイダーの追加
          </Button>
        )}
      </Flex>

      <Flex gap={8} wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="サービス名で探す"
          style={{ width: 280 }}
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onSearch={(v) => setFilters((f) => ({ ...f, q: v }))}
        />
        <Select
          allowClear
          placeholder="種別"
          style={{ width: 160 }}
          value={filters.kind || undefined}
          onChange={(v?: string) => setFilters((f) => ({ ...f, kind: v ?? "" }))}
          options={Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))}
        />
      </Flex>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="サービスプロバイダーを取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<ServiceProvider>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        loading={query.isFetching}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: "サービスプロバイダーはまだ無い" }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (t) => `全 ${t} 件`,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
      />

      <AddModal
        open={adding}
        onClose={() => setAdding(false)}
        onPick={(kind, initial) => {
          setAdding(false);
          setDrawer({ id: null, kind, initial });
        }}
      />
      <ProviderDrawer state={drawer} onClose={() => setDrawer(null)} />
    </div>
  );
}
